// Auto-tracking composable for Vue components
// Automatically tracks common user interactions

import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { 
  trackPageView, 
  trackScrollDepth, 
  trackTimeEngagement, 
  trackError,
  trackPerformance 
} from './gtm-tracking'

export function useAutoTracking() {
  const route = useRoute()
  const startTime = ref(Date.now())
  const scrollThresholds = ref(new Set())
  const hasTrackedPerformance = ref(false)

  // Track page view automatically
  const trackCurrentPage = () => {
    trackPageView(route.path, document.title, {
      page_category: getPageCategory(route.path),
      page_section: getPageSection(route.path),
      is_mobile: window.innerWidth < 768
    })
  }

  // Track scroll depth
  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    )
    
    // Track at 25%, 50%, 75%, 100% thresholds
    const threshold = Math.floor(scrollPercent / 25) * 25
    if (threshold > 0 && threshold <= 100 && !scrollThresholds.value.has(threshold)) {
      scrollThresholds.value.add(threshold)
      trackScrollDepth(threshold, route.path)
    }
  }

  // Track time on page
  const trackTimeOnPage = () => {
    const timeSpent = Math.round((Date.now() - startTime.value) / 1000)
    trackTimeEngagement(route.path, timeSpent, 'end')
  }

  // Track performance metrics
  const trackPagePerformance = () => {
    if (hasTrackedPerformance.value) return
    
    // Wait for page to fully load
    setTimeout(() => {
      if ('PerformanceObserver' in window) {
        // Track Core Web Vitals
        const performanceData = {}
        
        // First Contentful Paint
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              performanceData.fcp = entry.startTime
            }
          }
        }).observe({ entryTypes: ['paint'] })

        // Largest Contentful Paint
        new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1]
          performanceData.lcp = lastEntry.startTime
        }).observe({ entryTypes: ['largest-contentful-paint'] })

        // First Input Delay
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            performanceData.fid = entry.processingStart - entry.startTime
          }
        }).observe({ entryTypes: ['first-input'] })

        // Cumulative Layout Shift
        let clsValue = 0
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value
              performanceData.cls = clsValue
            }
          }
        }).observe({ entryTypes: ['layout-shift'] })

        // Send performance data after a delay
        setTimeout(() => {
          trackPerformance(performanceData)
          hasTrackedPerformance.value = true
        }, 3000)
      }
    }, 1000)
  }

  // Track JavaScript errors
  const trackJSErrors = () => {
    window.addEventListener('error', (event) => {
      trackError('javascript_error', event.message, event.filename)
    })

    window.addEventListener('unhandledrejection', (event) => {
      trackError('promise_rejection', event.reason, 'promise')
    })
  }

  // Helper functions
  const getPageCategory = (path) => {
    if (path === '/') return 'home'
    if (path.includes('/videos')) return 'videos'
    if (path.includes('/lab') || path.includes('/projects')) return 'projects'
    return 'other'
  }

  const getPageSection = (path) => {
    const parts = path.split('/').filter(Boolean)
    return parts[0] || 'home'
  }

  // Setup tracking on mount
  onMounted(() => {
    trackCurrentPage()
    trackPagePerformance()
    trackJSErrors()
    
    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true })
    
    // Track page start time
    trackTimeEngagement(route.path, 0, 'start')
  })

  // Cleanup on unmount
  onUnmounted(() => {
    trackTimeOnPage()
    window.removeEventListener('scroll', handleScroll)
  })

  return {
    trackCurrentPage,
    trackTimeOnPage
  }
}

// Session management utilities
export function initializeSession() {
  // Create session ID if it doesn't exist
  if (!sessionStorage.getItem('session_id')) {
    sessionStorage.setItem('session_id', `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`)
  }

  // Track session start
  if (!sessionStorage.getItem('session_started')) {
    sessionStorage.setItem('session_started', new Date().toISOString())
    
    // Track session info
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'session_start',
        session_id: sessionStorage.getItem('session_id'),
        is_returning_visitor: localStorage.getItem('has_visited') === 'true',
        visit_count: parseInt(localStorage.getItem('visit_count') || '0') + 1,
        days_since_last_visit: getDaysSinceLastVisit()
      })
    }

    // Mark as visited
    localStorage.setItem('has_visited', 'true')
    localStorage.setItem('last_visit', new Date().toISOString())
    localStorage.setItem('visit_count', parseInt(localStorage.getItem('visit_count') || '0') + 1)
  }
}

function getDaysSinceLastVisit() {
  const lastVisit = localStorage.getItem('last_visit')
  if (!lastVisit) return 0
  
  const lastVisitDate = new Date(lastVisit)
  const today = new Date()
  const diffTime = Math.abs(today - lastVisitDate)
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}
